import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Task } from '@app/_components/task';
import { TaskService } from '@app/_components/task';
import { AuthenticationService, UserService, AlertService } from '@app/_services';
import { User } from '@app/_models';
import { Subscription } from 'rxjs';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TaskComponent } from './task/task.component';
import { QuickTaskComponent } from './quick-task/quick-task.component';
import { InboxComponent } from '@app/_components/inbox/inbox/inbox.component';
import { Inbox } from '@app/_components/inbox';
import { first } from 'rxjs/operators';
import { Location } from '@angular/common';

/**
 * Base class for all task view components (table, board, calendar, gantt)
 * Contains all common functionality to eliminate code duplication
 */
@Component({
    selector: 'app-base-task-view',
    template: '',
    standalone: false
})
export abstract class BaseTaskViewComponent implements OnInit, OnChanges, OnDestroy {
  // Common Input/Output properties
  @Input() tasks: Task[] = [];
  @Input() taskTag: any;
  @Input() groupBy: string = 'none';
  @Input() filters: any = {};
  @Output() taskClick = new EventEmitter<Task>();
  @Output() taskDoubleClick = new EventEmitter<Task>();
  @Output() filtersProcessed = new EventEmitter<void>();

  // Common properties
  currentUser: User;
  currentUserSubscription: Subscription;
  usersSearch: any[] = [];
  selectedUsers: any[] = [];
  private taskSummary: any = {};
  private taskSummarySubscription: Subscription;
  private dataLoaded: boolean = false; // Flag to prevent duplicate API calls
  taskSummaryConfig: any[] = [
    { label: 'Assigned to me', param: 'me', property: 'taskForUser' },
    { label: 'Due for Today', param: 'today', property: 'taskToday' },
    { label: 'Due for this week', param: 'week', property: 'taskThisWeek' },
    { label: 'Due for this Month', param: 'month', property: 'taskThisMonth' },
    { label: 'Follow up by me', param: 'me', property: 'taskForUser' },
    { label: 'Follow up Today', param: 'followuptoday', property: 'taskFollowupToday' },
    { label: 'Follow up this week', param: 'followupweek', property: 'taskFollowupThisWeek' },
    { label: 'Follow up Month', param: 'followupmonth', property: 'taskFollowupThisMonth' }
  ];
  contextMenuItems: any[] = [];

  constructor(
    protected authenticationService: AuthenticationService,
    protected taskService: TaskService,
    protected userService: UserService,
    protected alertService: AlertService,
    protected dialog: MatDialog,
    protected location: Location
  ) {
    // Get current user value if already available
    this.currentUser = this.authenticationService.currentUserValue;
    
    // If user is already available, load data immediately
    if (this.currentUser) {
      this.loadUsers();
      this.refreshSummaryCounts();
      this.dataLoaded = true;
    }
    
    // Subscribe to user changes for future updates
    this.currentUserSubscription = this.authenticationService.currentUser.subscribe(user => {
      if (user) {
        // Only load if user changed (different userId) and we haven't loaded yet, or if user actually changed
        const userIdChanged = !this.currentUser || this.currentUser.userId !== user.userId;
        if (userIdChanged && !this.dataLoaded) {
          this.currentUser = user;
          this.loadUsers();
          this.refreshSummaryCounts();
          this.dataLoaded = true;
        } else if (userIdChanged) {
          // User changed after initial load
          this.currentUser = user;
          this.loadUsers();
          this.refreshSummaryCounts();
        } else {
          this.currentUser = user;
        }
      }
    });


    this.contextMenuItems = [
      {
        label: 'Copy Task',
        icon: 'pi pi-fw pi-copy',
        command: (event) => this.openTaskForClone()
      },
      {
        label: 'Sub Task',
        icon: 'pi pi-fw pi-clone',
        command: (event) => this.openTaskForSubTask()
      },
      {
        label: 'Mail Task',
        icon: 'pi pi-fw pi-envelope',
        command: (event) => this.openMailTask()
      },
      {
        label: 'Summary',
        icon: 'pi pi-fw pi-search',
        command: (event) => this.openSummary()
      },
      {
        label: 'Status Update req',
        icon: 'pi pi-question',
        command: (event) => this.sendStatusUpdateReq(this.selectedTask)
      },
    ];
  }

  ngOnInit() {
    // onInitView is called here, but data loading is handled in constructor
    // to avoid duplicate API calls
    this.onInitView();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tasks'] || changes['filters'] || changes['groupBy']) {
      this.onViewChanges(changes);
      // Emit that filters have been processed
      if (changes['filters'] || changes['tasks']) {
        setTimeout(() => {
          this.filtersProcessed.emit();
        }, 100);
      }
    }
  }

  ngOnDestroy() {
    if (this.currentUserSubscription) {
      this.currentUserSubscription.unsubscribe();
    }
    if (this.taskSummarySubscription) {
      this.taskSummarySubscription.unsubscribe();
    }
  }

  // Abstract methods for view-specific implementations
  protected abstract onInitView(): void;
  protected abstract onViewChanges(changes: SimpleChanges): void;

  // Common methods
  getTaskSummaryItems(): any[] {
    return this.taskSummaryConfig.map(item => ({
      ...item,
      value: this.taskSummary?.[item.property] || 0
    }));
  }

  applyFilters(tasks: Task[]): Task[] {
    if (!this.filters || Object.keys(this.filters).length === 0) {
      return tasks;
    }

    return tasks.filter(task => {
      // Filter by taskId (from category selection)
      if (this.filters.taskId && Array.isArray(this.filters.taskId) && this.filters.taskId.length > 0) {
        if (!this.filters.taskId.includes(task.taskId)) {
          return false;
        }
      }

      // Filter by name
      if (this.filters.name && !task.name.toLowerCase().includes(this.filters.name.toLowerCase())) {
        return false;
      }

      // Filter by status
      if (this.filters.status && Array.isArray(this.filters.status) && this.filters.status.length > 0) {
        if (!this.filters.status.includes((task as any).status)) {
          return false;
        }
      }

      // Filter by priority
      if (this.filters.priority && Array.isArray(this.filters.priority) && this.filters.priority.length > 0) {
        if (!this.filters.priority.includes((task as any).priority)) {
          return false;
        }
      }

      // Filter by assignee
      if (this.filters.assignedTo) {
        if (Array.isArray(this.filters.assignedTo)) {
          const userIds = this.filters.assignedTo.map(u => u.userId || u);
          if (!userIds.includes(task.assignedTo?.userId)) {
            return false;
          }
        } else {
          if (task.assignedTo?.userId !== this.filters.assignedTo.userId) {
            return false;
          }
        }
      }

      // Filter by created by
      if (this.filters.createdBy) {
        if (Array.isArray(this.filters.createdBy)) {
          const userIds = this.filters.createdBy.map(u => u.userId || u);
          if (!userIds.includes((task as any).createdBy?.userId)) {
            return false;
          }
        } else {
          if ((task as any).createdBy?.userId !== this.filters.createdBy.userId) {
            return false;
          }
        }
      }

      // Filter by requested by
      if (this.filters.requestedBy) {
        if (Array.isArray(this.filters.requestedBy)) {
          const userIds = this.filters.requestedBy.map(u => u.userId || u);
          if (!userIds.includes((task as any).requestedBy?.userId)) {
            return false;
          }
        } else {
          if ((task as any).requestedBy?.userId !== this.filters.requestedBy.userId) {
            return false;
          }
        }
      }

      // Filter by date ranges
      if (this.filters.dateDue && Array.isArray(this.filters.dateDue) && this.filters.dateDue.length === 2) {
        const dueDate = task.dateDue ? new Date(task.dateDue) : null;
        if (!dueDate || dueDate < this.filters.dateDue[0] || dueDate > this.filters.dateDue[1]) {
          return false;
        }
      }

      return true;
    });
  }

  fullName(user: any): string {
    if (!user) return '';
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || '';
  }

  onTaskClick(task: Task, event: Event) {
    event.stopPropagation();
    // Change URL to match list view behavior
    if (task && task.taskId) {
      this.location.go('/task-view/' + task.taskId);
    }
    this.taskClick.emit(task);
  }

  onTaskDoubleClick(task: Task, event: Event) {
    event.stopPropagation();
    this.taskDoubleClick.emit(task);
  }

  addTask(tasktypeId: any) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '100vw';
    dialogConfig.height = '100vh';
    dialogConfig.maxWidth = '100vw';
    dialogConfig.maxHeight = '100vh';
    dialogConfig.data = {
      title: 'Add Task',
      taskTypeId: tasktypeId,
      isModal: true,
      selectedTagForCreate: this.taskTag
    };

    const dialogRef: MatDialogRef<TaskComponent> = this.dialog.open(TaskComponent, dialogConfig);

    if (dialogRef.componentInstance) {
      dialogRef.componentInstance.doAdd.subscribe(
        data => {
          console.log('Dialog output:', data);
          // Emit event to parent to refresh tasks
        });
      dialogRef.componentInstance.doUpdate.subscribe(
        data => {
          console.log('Dialog output:', data);
        });
    }
  }

  addQuickTask(tasktypeId: any) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.width = '100%';
    dialogConfig.height = '100%';
    dialogConfig.maxWidth = '1600px';
    dialogConfig.maxHeight = '800px';
    dialogConfig.data = {
      title: 'Add Task',
      taskTypeId: tasktypeId,
      isModal: true,
      selectedTagForCreate: this.taskTag
    };

    const dialogRef: MatDialogRef<QuickTaskComponent> = this.dialog.open(QuickTaskComponent, dialogConfig);

    if (dialogRef.componentInstance) {
      dialogRef.componentInstance.doAdd.subscribe(
        data => {
          console.log('Dialog output:', data);
          dialogRef.close();
        });
      dialogRef.componentInstance.doUpdate.subscribe(
        data => {
          console.log('Dialog output:', data);
        });
    }
  }

  loadUsers() {
    this.userService.findUsersWith('').pipe(first()).subscribe(
      data => {
        this.usersSearch = [];
        this.usersSearch = data.data.userSummaryList;
        this.usersSearch.forEach(user => {
          if (!user.lastName) { user.lastName = ''; }
          if (!user.firstName) { user.firstName = ''; }
          user.fullName = user.lastName + ' ' + user.firstName;
        });
      },
      error => {
        this.alertService.error(error);
      }
    );
  }

  onPeopleFilterChange(selectedUserIds: any[]) {
    this.selectedUsers = selectedUserIds;
    // Apply filter to tasks - this will need to be handled by parent component
  }

  onTagSelect(event: any) {
    this.taskTag = event;
    // Emit event to parent to handle tag filtering
  }

  private refreshSummaryCounts() {
    if (!this.currentUser) return;
    
    this.taskSummarySubscription = this.taskService.currentTaskSummary.subscribe(taskSummary => {
      this.taskSummary = taskSummary;
    });
    
    this.taskService.getTaskSummary(this.currentUser.userId).pipe(first()).subscribe();
  }

  loadWithParam(param: string) {
    // Handle summary item click - emit to parent to handle filtering
  }

  reloadSummaryCount() {
    if (this.currentUser) {
      this.taskService.getTaskSummary(this.currentUser.userId).pipe(first()).subscribe();
    }
  }

  // Sub-task helper methods
  /**
   * Check if a task is a sub-task (has a parent)
   */
  isSubTask(task: Task): boolean {
    return !!(task as any).parentTaskId || !!(task as any).ischild || !!(task as any).parentTask || !!(task as any).parent;
  }

  /**
   * Get the parent task ID for a sub-task
   */
  getParentTaskId(task: Task): number | null {
    return (task as any).parentTaskId || (task as any).parentTask?.taskId || (task as any).parent?.taskId || null;
  }

  /**
   * Get the parent task object if available
   */
  getParentTask(task: Task): Task | null {
    return (task as any).parentTask || (task as any).parent || null;
  }

  /**
   * Find parent task from the tasks array
   */
  findParentTask(task: Task, allTasks: Task[]): Task | null {
    const parentTaskId = this.getParentTaskId(task);
    if (!parentTaskId) return null;
    return allTasks.find(t => t.taskId === parentTaskId) || null;
  }

  /**
   * Get all sub-tasks for a given parent task
   */
  getSubTasks(parentTask: Task, allTasks: Task[]): Task[] {
    return allTasks.filter(t => this.getParentTaskId(t) === parentTask.taskId);
  }

  /**
   * Check if a task has sub-tasks
   */
  hasSubTasks(task: Task, allTasks: Task[]): boolean {
    return this.getSubTasks(task, allTasks).length > 0;
  }

  /**
   * Get display name for parent task reference
   */
  getParentTaskDisplayName(task: Task): string {
    const parentTask = this.getParentTask(task);
    if (parentTask) {
      return `#${parentTask.taskId}: ${parentTask.name || 'Untitled'}`;
    }
    const parentTaskId = this.getParentTaskId(task);
    if (parentTaskId) {
      return `Parent: #${parentTaskId}`;
    }
    return '';
  }

  /**
   * Open parent task in dialog
   */
  onParentTaskClick(task: Task, event: Event): void {
    event.stopPropagation(); // Prevent triggering the task click event
    const parentTaskId = this.getParentTaskId(task);
    if (!parentTaskId) return;

    // Change URL to match list view behavior
    this.location.go('/task-view/' + parentTaskId);

    // Find parent task from current tasks
    let parentTask = this.findParentTask(task, this.tasks);
    
    // If parent task not found in current tasks, try to get it from the task object itself
    if (!parentTask) {
      parentTask = this.getParentTask(task);
    }
    
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.maxWidth = "100vw";
    dialogConfig.width = "100vw";
    dialogConfig.maxWidth = "100vw";
    dialogConfig.maxHeight = "100vh";
    dialogConfig.data = {
      task: parentTask,
      title: '',
      taskApi: parentTask || { taskId: parentTaskId },
      isModal: true
    };

    const dialogRef = this.dialog.open(TaskComponent, dialogConfig);
    
    // Handle task updates and reset URL
    if (dialogRef.componentInstance) {
      dialogRef.componentInstance.doUpdate.subscribe(
        data => {
          console.log('Parent task updated:', data);
          this.location.go('/');
        });
      dialogRef.componentInstance.doAdd.subscribe(
        data => {
          console.log('Parent task added:', data);
          this.location.go('/');
        });
    }
    
    // Reset URL when dialog closes
    dialogRef.afterClosed().subscribe(result => {
      this.location.go('/');
    });
  }

  // Context menu methods - common functionality for all task views
  selectedTask: Task | null = null;
  showOpenSummary = false;
  taskApiData: any = null;

  /**
   * Open context menu for a task
   */
  openCm(event: Event, cm: any, task: Task): boolean {
    event.preventDefault();
    event.stopPropagation();
    this.selectedTask = task;
    cm.show(event);
    return false;
  }

  /**
   * Open task summary dialog
   */
  openSummary(): void {
    if (this.selectedTask) {
      this.taskApiData = this.selectedTask;
      this.showOpenSummary = true;
    }
  }

  /**
   * Open mail task dialog
   */
  openMailTask(): void {
    if (!this.selectedTask) return;
    
    const taskApi = this.selectedTask;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.width = '100%';
    dialogConfig.height = '100%';
    dialogConfig.maxWidth = '1600px';
    dialogConfig.maxHeight = '800px';
    
    const inbox = new Inbox();
    inbox.subject = taskApi.name;
    inbox.senderEmail = taskApi?.assignedTo?.emailAddress;
    inbox.emailHtml = this.getTaskMailContent(taskApi);
    inbox.emailText = this.getTaskMailContent(taskApi);
    
    dialogConfig.data = {
      inbox: inbox,
      title: 'Mail Task Details',
    };

    const dialogRef = this.dialog.open(InboxComponent, dialogConfig);

    if (dialogRef.componentInstance) {
      dialogRef.componentInstance.doAdd.subscribe(
        data => {
          this.alertService.success('Mail Sent', true);
          console.log('Dialog output:', data);
          dialogRef.close();
        });
      dialogRef.componentInstance.doUpdate.subscribe(
        data => {
          this.alertService.success('Mail Sent', true);
          console.log('Dialog output:', data);
          dialogRef.close();
        });
    }
  }

  /**
   * Get task mail content HTML
   */
  getTaskMailContent(taskApi: any): string {
    const taskLink = 'https://admin.kbmhawaii.com/task-view/' + taskApi?.taskId;
    const emailHTML = '<h4>Hi, Please find task details</h4> <br><br><table>' +
      '<tr><th colspan="1">Task assigned</th><th colspan="5"><a href="' + taskLink + '" target="_blank">Click here to view task</a></th></tr>' +
      '<tr><td>Task Name</td><td> ' + this.makeSafe(taskApi.name) + '</td></tr>' +
      '<tr><td>Due Date</td><td> ' + this.makeSafe(taskApi.dateDue) + '</td></tr>' +
      '<tr><td>Created By</td><td> ' + this.makeSafe(taskApi?.createdBy?.lastName) + ' , ' + this.makeSafe(taskApi?.createdBy?.firstName) + '</td></tr>' +
      '<tr><td>Owned By</td><td> ' + this.makeSafe(taskApi?.ownedBy?.lastName) + ' , ' + this.makeSafe(taskApi?.ownedBy?.firstName) + '</td></tr>' +
      '<tr><td>Due Date</td><td> ' + this.makeSafe(taskApi.dateDue) + '</td></tr>' +
      '<tr><td>Status</td><td> ' + this.getStatusName(taskApi) + '</td></tr>' +
      '<tr><td>Doc</td><td> ' + this.makeSafe(taskApi?.miniDoc) + '</td></tr>' +
      '</table>';
    return emailHTML;
  }

  /**
   * Make value safe for HTML
   */
  makeSafe(val: any): string {
    return val ? val : '';
  }

  /**
   * Get status name for a task
   */
  getStatusName(taskApi: any): string {
    if (taskApi && taskApi?.status !== undefined) {
      const status = this.taskService.statuses?.find((e: any) => e.value === taskApi?.status);
      return status ? status.name : '';
    }
    return '';
  }

  /**
   * Open task for clone (copy task)
   */
  openTaskForClone(): void {
    if (!this.selectedTask) return;
    
    const task = this.selectedTask;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.maxWidth = "100vw";
    dialogConfig.width = "100vw";
    dialogConfig.maxWidth = "100vw";
    dialogConfig.maxHeight = "100vh";

    const passingTask: any = new Task();
    Object.assign(passingTask, task);
    passingTask.jsonChildTask = null;
    passingTask.taskId = null;
    passingTask.miniDoc = null;
    
    dialogConfig.data = {
      title: 'Copy Task',
      isModal: true,
      taskApi: passingTask,
      purpose: 'clone',
      copyfromtaskid: task.taskId
    };

    const dialogRef = this.dialog.open(TaskComponent, dialogConfig);

    if (dialogRef.componentInstance) {
      dialogRef.componentInstance.doAdd.subscribe(
        data => {
          console.log('Dialog output:', data);
        });
      dialogRef.componentInstance.doUpdate.subscribe(
        data => {
          console.log('Dialog output:', data);
        });
      dialogRef.componentInstance.doaddAndDontClose.subscribe(
        data => {
          console.log('Dialog output:', data);
        });
    }
  }

  /**
   * Open task for sub task creation
   */
  openTaskForSubTask(): void {
    if (!this.selectedTask) return;
    
    const task = this.selectedTask;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.maxWidth = "100vw";
    dialogConfig.width = "100vw";
    dialogConfig.maxWidth = "100vw";
    dialogConfig.maxHeight = "100vh";

    const passingTask: any = new Task();
    Object.assign(passingTask, task);
    passingTask.name = '';
    passingTask.taskId = null;
    passingTask.parentTaskId = task?.taskId;
    passingTask.msgId = (task as any).msgId;
    passingTask.jsonChildTask = null;
    passingTask.miniDoc = null;
    
    dialogConfig.data = {
      title: 'Assign Task',
      isModal: true,
      taskApi: passingTask,
      purpose: 'clone',
      copyfromtaskid: task?.taskId
    };

    const dialogRef = this.dialog.open(TaskComponent, dialogConfig);

    if (dialogRef.componentInstance) {
      dialogRef.componentInstance.doAdd.subscribe(
        data => {
          console.log('Dialog output:', data);
        });
      dialogRef.componentInstance.doUpdate.subscribe(
        data => {
          console.log('Dialog output:', data);
        });
      dialogRef.componentInstance.doaddAndDontClose.subscribe(
        data => {
          console.log('Dialog output:', data);
        });
    }
  }

  /**
   * Send status update request
   */
  sendStatusUpdateReq(task: Task): void {
    if (!task) return;
    
    this.taskService.sendStatusUpdateReq(task)
      .pipe(first())
      .subscribe(
        res => {
          this.alertService.success(res, true);
          console.log('Task-Update SUCCESS: ' + JSON.stringify(res));
        },
        err => {
          console.log('Task-Update FAIL: ' + JSON.stringify(err));
          this.alertService.error(err);
        }
      );
  }
}

